// inperson-math — single source of truth del deck interno de la vertical
// In-Person Payments (/in-person-payments). Todas las slides importan de aquí;
// ninguna hardcodea un número propio (mismo patrón que bancoppel-math: evita
// drift entre la aritmética visible y los totales que la citan).
//
// Caso: BanCoppel mueve ~10M transacciones card-present al mes. Yuno cobra
// ~$0.02 USD por transacción más un fee de implementación de ~$50,000 USD.
// El deck es INTERNO (C-Level de Yuno), así que todo va en USD.

// ── Inputs (confirmados por el AE) ───────────────────────────────────────
// Los 10M tx/mes son el volumen card-present del acuerdo; NO se suman al
// volumen del BC de MDR (/bc-bancoppel) — lo incluyen.
export const MONTHLY_TX = 10_000_000
export const FEE_USD_PER_TX = 0.02
export const IMPLEMENTATION_FEE_USD = 50_000

// Ventana de entrada: BanCoppel migra de terminales de EVO a terminales
// Ingenico propias; al cerrar la migración todo el parque de retail es suyo.
export const TERMINAL_MIGRATION_CLOSE = 'Q4 2026'
export const TERMINAL_VENDOR_TODAY = 'EVO'
export const TERMINAL_VENDOR_TARGET = 'Ingenico'

// ── Derivados ────────────────────────────────────────────────────────────
export const MRR_USD = MONTHLY_TX * FEE_USD_PER_TX
export const ARR_USD = MRR_USD * 12
export const YEAR_ONE_USD = ARR_USD + IMPLEMENTATION_FEE_USD
export const ANNUAL_TX = MONTHLY_TX * 12

// ── Calendario del proyecto (confirmado 2026-08-06) ──────────────────────
// El ARR de arriba es el ESTADO DE RÉGIMEN; esto dice CUÁNDO se llega.
// Q4 2026 producto · Q1 2027 tests · Q2 2027 arranca el rollout · Q3 2027
// sigue la rampa. La rampa sube 1M tx por mes: mes 1 = 1M, mes 3 = 3M y, tres
// meses después, el piso de 6M.
//
// El rango hablado del mes 6 es 6–7M tx/mes. La math usa el PISO (6M):
// promediar a 6.5M inflaría el run-rate de salida sin dato que lo sostenga.
// RAMP_CEILING_TX solo existe para mostrar el rango en el copy.
export const ROLLOUT_START = 'Q2 2027'
export const RAMP_STEP_TX = 1_000_000
export const RAMP_MONTHS = 6
export const RAMP_CEILING_TX = 7_000_000

export const ROLLOUT_RAMP = Array.from({ length: RAMP_MONTHS }, (_, i) => {
  const tx = RAMP_STEP_TX * (i + 1)
  return {
    month: i + 1,
    quarter: i < 3 ? 'Q2 2027' : 'Q3 2027',
    tx,
    usd: tx * FEE_USD_PER_TX,
  }
})

export const RAMP_TOTAL_TX = ROLLOUT_RAMP.reduce((acc, m) => acc + m.tx, 0)
export const RAMP_TOTAL_USD = ROLLOUT_RAMP.reduce((acc, m) => acc + m.usd, 0)

// Run-rate al cerrar la rampa (mes 6) contra el estado de régimen de 10M/mes.
export const EXIT_MONTHLY_TX = ROLLOUT_RAMP[RAMP_MONTHS - 1].tx
export const EXIT_MRR_USD = EXIT_MONTHLY_TX * FEE_USD_PER_TX
export const EXIT_ARR_USD = EXIT_MRR_USD * 12
export const EXIT_PCT_OF_ARR = Math.round((EXIT_ARR_USD / ARR_USD) * 100)

// Primeros clientes del pipeline de la vertical. El nombre es dato; la
// etiqueta de país se localiza en inperson-copy (countries[code]).
export const TIMELINE_PIPELINE = [
  { name: 'Redeban', country: 'pe' },
  { name: 'Quálitas', country: 'mx' },
  { name: 'McDonald’s', country: 'mx' },
]

// ── Inputs que siguen ABIERTOS ───────────────────────────────────────────
// Se muestran como abiertos en el deck, no se estiman a ojo. El ticket
// promedio card-present NO es el $110 USD del BC de MDR (ese es el caso
// digital), por eso este deck no calcula bps ni % sobre el ticket.
export const OPEN_INPUTS = [
  {
    key: 'recert_cost',
    label: 'Costo de la recertificación',
    detail: 'Depende de lo que cobren las marcas por sumar a Yuno como eslabón del flujo. Puede no tener costo; si lo tiene, será muy inferior a certificar desde cero.',
  },
  {
    key: 'integration_route',
    label: 'Ruta de integración',
    detail: 'Back-to-back o SDK en la terminal. Define el alcance técnico, el esfuerzo de ingeniería y el fee de implementación. Faltan reuniones técnicas.',
  },
  {
    key: 'avg_ticket',
    label: 'Ticket promedio card-present',
    detail: 'Falta para dimensionar el ahorro de MDR del lado del cliente. El ticket del caso digital no aplica al retail en tienda.',
  },
]

// ── Formatters ───────────────────────────────────────────────────────────
export const int = (v) => Math.round(v).toLocaleString('en-US')

export const usd = (v) => `$${int(v)}`

export function millionsUsd(v, decimals = 1) {
  return `$${(v / 1_000_000).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`
}

// "10M" / "120M" — para titulares de volumen.
export function compactTx(v) {
  if (v >= 1_000_000) {
    const m = v / 1_000_000
    const s = m.toLocaleString('en-US', { maximumFractionDigits: 1 })
    return `${s}M`
  }
  return int(v)
}

// $0.02 — el fee por transacción necesita 2 decimales, no separador de miles.
export const centsUsd = (v) =>
  `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
